import { MatFormFieldAppearance } from '@angular/material/form-field';
import { TooltipPosition } from '@angular/material/tooltip';
import {
  AppDatepicker,
  AppFormFieldConfig,
  AppTextarea,
  AppTextbox,
  ButtonConfig,
  CheckboxConfig,
  InputType,
  RadioButtonConfig,
  slideToggle,
  TextBoxIconConfig,
  ITextValueOption,
} from '@waseel-asp/tpa-waseel-lib-staging';
import { generateGUID } from './guid-function';

export type CallbackFunction = (event?, index?) => void;

export function getTextboxConfig(
  label: string,
  formControlName: string,
  value?: string | number,
  type?: InputType,
  appearance?: MatFormFieldAppearance,
  placeholder?,
  disableCallBack?,
  prefixIcon?,
  suffixIcon?,
  hintText?,
  imgUrl?: TextBoxIconConfig,
  prefixIconList?,
  suffixIconList?,
  change?: CallbackFunction,
  keyup?: CallbackFunction,
  keypress?: CallbackFunction,
  blur?: CallbackFunction,
  isSuffixImg?,
  isPrefixImg?,
  textSuffix?,
  textSuffixSelect?,
  isPlusMinusVisible?,
  restrictDecimalTo?,
  isFloatLabel = true,
  maskingPattern?
) {
  const appTextBox: AppTextbox = {
    label,
    formControlName,
    type: type ?? InputType.text,
    prefixIcon,
    suffixIcon,
    placeholder,
    hintText,
    imgUrl,
    change,
    keyup,
    keypress,
    prefixIconList,
    suffixIconList,
    blur,
    appearance: appearance ?? 'outline',
    disableCallBack,
    isSuffixImg,
    isPrefixImg,
    textSuffix,
    value,
    textSuffixSelect,
    isPlusMinusVisible,
    restrictDecimalTo,
    isFloatLabel,
    maskingPattern
  };
  if (change) change();
  if (keyup) keyup();
  if (keypress) keypress();
  if (blur) blur();
  return appTextBox;
}

export function getButtonConfig(
  callback: CallbackFunction,
  id: string,
  variant: 'raised' | 'flat' | 'stroked' | 'fab' | 'icon' | 'basic',
  color: 'basic' | 'primary' | 'warn' | 'accent',
  buttonText?: string,
  isPrimary?: boolean,
  disableCallBack?: () => boolean,
  icon?: string,
  iconUrl?: string,
  tooltipText?: string,
  tooltipPosition?: TooltipPosition,
  cssClasses?: string[],
  type?: 'submit' | 'reset' | 'button',
  visibleCallback?: (element) => boolean,
) {
  const appButton: ButtonConfig = {
    id: id ? id + generateGUID() : generateGUID(),
    variant,
    color,
    icon,
    iconUrl,
    isPrimary,
    buttonText,
    tooltipText,
    tooltipPosition,
    disableCallBack,
    cssClasses,
    callback,
    type,
    visibleCallback
  };
  return appButton;
}

export function getTextboxAreaConfig(
  label: string,
  formControlName: string,
  appearance?: MatFormFieldAppearance,
  autoSizeMinRows?: number,
  autoSizeMaxRows?: number,
  placeholder?: string,
  id?: string,
  isFloatLabel?: boolean,
  keyup?: CallbackFunction,
  keydown?: CallbackFunction,
  blur?: CallbackFunction,
  change?: CallbackFunction
) {
  const appTextBox: AppTextarea = {
    label,
    formControlName,
    id: id ? id + generateGUID() : generateGUID(),
    appearance,
    placeholder,
    autoSizeMinRows,
    autoSizeMaxRows,
    isFloatLabel,
    keyup,
    keydown,
    blur,
    change,
  };
  if (change) change();
  if (keyup) keyup();
  if (keydown) keydown();
  if (blur) blur();
  return appTextBox;
}

export function getDatePickerConfig(
  formControlName,
  label,
  min?: () => Date,
  max?: () => Date,
  onChangeDate?: CallbackFunction,
  appearance: MatFormFieldAppearance = 'outline',
  hint?: string,
  langCode?: string,
  placeholder?: string,
  isFloatLabel?: boolean,
  value?: string | Date,
  id?: string,
  isDisable?: boolean,
  imgUrl?: string,
  setDayEndTime?: boolean,
  dateFilter?: (d: Date | null) => boolean
) {
  const dateConfig: AppDatepicker = {
    formControlName,
    label,
    min,
    max,
    onChangeDate,
    appearance,
    hint,
    langCode,
    placeholder,
    isFloatLabel,
    value,
    id: id ? id + generateGUID() : generateGUID(),
    isDisable,
    imgUrl: imgUrl ?? "./assets/images/ic-calendar.svg",
    setDayEndTime,
    dateFilter
  };
  if (min) min();
  if (max) max();
  if (onChangeDate) onChangeDate();
  return dateConfig;
}

export function getDateTimePickerConfig(
  formControlName: string,
  label: string,
  min?: () => Date,
  max?: () => Date,
  onChangeDate?: CallbackFunction,
  placeholder?: string,
  appearance: MatFormFieldAppearance = 'outline',
  id?: string,
  disabled?: boolean,
  isFloatLabel?: boolean,
  langCode?: string
) {
  const dateTimeConfig = {
    formControlName,
    label,
    min,
    max,
    onChangeDate,
    placeholder,
    appearance,
    id: id ? id + generateGUID() : generateGUID(),
    disabled,
    isFloatLabel,
    langCode
  };
  if (min) min();
  if (max) max();
  if (onChangeDate) onChangeDate();
  return dateTimeConfig;
}

export function getCheckBoxConfig(
  formControlName: string,
  label: string,
  appearance: MatFormFieldAppearance = 'outline',
  labelPosition: 'before' | 'after',
  change?: CallbackFunction,
  disableCallBack?: () => boolean,
) {
  const checkBoxConfig: CheckboxConfig = { formControlName, label, appearance, labelPosition, change, disableCallBack };
  return checkBoxConfig;
}

export function getSlideToggleConfig(
  formControlName: string,
  label: string,
  labelPosition?: 'before' | 'after',
  color?: 'primary' | 'accent' | 'warn',
  change?,
  isDisable?,
  id?: string,
) {
  const slideToggleConfig: slideToggle = { formControlName, label, labelPosition, color, change, isDisable, id: id ? id + generateGUID() : generateGUID() };
  return slideToggleConfig;
}

export function getRadioButtonConfig(
  formControlName: string,
  label: string,
  options?: ITextValueOption[],
  change?: (event?: any) => void,
  appearance: MatFormFieldAppearance = 'outline',
  orientation: 'horizontal' | 'vertical' = 'horizontal',
  id?: string,
  labelCssStyles?: string[]
) {
  const checkBoxConfig: RadioButtonConfig = { formControlName, label, options, change, appearance, orientation, id: id ? id + generateGUID() : generateGUID(), labelCssStyles };
  if (change) change();
  return checkBoxConfig;
}

export function getTextEditorConfig(label: string, formControlName: string) {
  const textEditorConfig: AppFormFieldConfig = { label, formControlName };
  return textEditorConfig;
}